/**
 * @file underscore扩展
 * @author Justineo(justice360@gmail.com)
 */

import {each, isObject, isFunction, mapKeys} from 'lodash';

/**
 * 清理对象中无用的键值对
 *
 * 默认会去除所有值为`null`、`undefined`以及空字符串`""`的键值对
 *
 * 如果提供了`defaults`参数，则额外去除值与`defaults`的同名属性相同的键值对
 *
 * @param {Object} object 输入的对象
 * @param {Object} [defaults] 用于提供属性默认值的参照对象
 * @param {boolean} [deep=false] 是否深度清理，即遇到属性值为对象继续递归清理
 * @return {Object} 清理后的新对象
 */
export function purify(object, defaults = {}, deep) {
    let purifiedObject = {};
    each(
        object,
        function (value, key) {
            let isDefaultNull = value == null || value === '';
            let isInDefaults = defaults.hasOwnProperty(key) && defaults[key] === value;
            if (!isDefaultNull && !isInDefaults) {
                if (deep && isObject(value)) {
                    purifiedObject[key] = purify(value, defaults[key], deep);
                }
                else {
                    purifiedObject[key] = value;
                }
            }
        }
    );
    return purifiedObject;
}

/**
 * 根据指定的映射关系修改对象的键名
 *
 * 如果键名不在给定的映射关系中，保留原名
 *
 * @param {Object} obj 输入的对象
 * @param {Object.<string, string>|Function} map 键名的映射关系或函数
 * @return {Object} 转换过的新对象
 */
export function mapKey(obj, map) {
    let result = {};
    if (obj == null) {
        return result;
    }
    if (isFunction(map)) {
        return mapKeys(obj, map);
    }
    each(obj, (value, key) => {
        if (map[key]) {
            result[map[key]] = value;
        }
        else {
            result[key] = value;
        }
    });
    return result;
}

/**
 * 判断两个像数字一样的参数是否相等
 *
 * @param {number|string} num1 第一个像数字的参数
 * @param {number|string} num2 第二个像数字的参数
 * @return {boolean}
 */
export function isNumberLikeEqual(num1, num2) {
    return '' + num1 === '' + num2;
}

/**
 * 一个英文字母算1个，一个中文算 factor 个
 *
 * @param {string} str 需要计算长度的字符串
 * @param {number} factor 中文长度，默认2
 * @return {number} 字符串长度
 */
export function getStringLength(str, factor = 2) {
    return str.replace(/[^\x00-\xff]/g, Array(factor + 1).join('x')).length;
}

/**
 * 解析 JSON String
 *
 * @param {string} str json string
 * @return {Object} json data
 */
export function parseJsonData(str) {
    try {
        return JSON.parse(str);
    }
    catch (e) {
        return null;
    }
}
